// ============================================================
// MongoDBSchemaDesign.jsx
//
// A branded, technical guide to Document Modeling:
// Embedding vs. Referencing, One-to-Many relationships,
// and the "Data Access Pattern" philosophy.
//
// Brand palette:
//   Background  → #001E2B  (MongoDB Deep Navy)
//   Primary     → #00ED64  (MongoDB Spring Green)
//   Accent      → #00E0FF  (Cyan / Logic)
//   Text        → #FFFFFF  (White)
//   Code        → #010409  (GitHub Dark)
// ============================================================

import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Paper,
    Stack,
    IconButton,
    Avatar,
    Button,
    Tooltip
} from "@mui/material";

/* MUI Icons */
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import LayersIcon from "@mui/icons-material/Layers";
import DynamicFormIcon from "@mui/icons-material/DynamicForm";
import SchoolIcon from "@mui/icons-material/School";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneIcon from "@mui/icons-material/Done";
import StorageIcon from "@mui/icons-material/Storage";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import EngineeringIcon from "@mui/icons-material/Engineering";

/* ── Brand tokens ────────────────────────────────────────── */
const BRAND = {
    bg: "#001E2B",
    surface: "rgba(255,255,255,0.04)",
    border: "rgba(0, 237, 100, 0.2)",
    green: "#00ED64",
    cyan: "#00E0FF",
    white: "#FFFFFF",
    muted: "rgba(255,255,255,0.6)",
    codeBg: "#010409",
};

/* ── Sub-Components ──────────────────────────────────────── */

function CodeTerminal({ code, label, color = BRAND.green }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    return (
        <Box sx={{ background: BRAND.codeBg, borderRadius: 3, border: `1px solid ${BRAND.border}`, overflow: "hidden", mb: 4 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", justifyContent: "space-between", background: "rgba(255,255,255,0.02)" }}>
                <Typography variant="caption" sx={{ color: color, fontWeight: 700, fontFamily: "monospace" }}>{label}</Typography>
                <IconButton size="small" onClick={handleCopy} sx={{ color: BRAND.muted }}>
                    {copied ? <DoneIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                </IconButton>
            </Box>
            <Box sx={{ p: 2.5, overflowX: "auto" }}>
                <Typography component="pre" sx={{ fontFamily: "'Fira Code', monospace", fontSize: 13, color: "#D1D5DB", lineHeight: 1.5 }}>
                    {code}
                </Typography>
            </Box>
        </Box>
    );
}

/* ── Main Component ──────────────────────────────────────── */

export default function MongoDBSchemaDesign() {
    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 6, md: 10 }, color: BRAND.white }}>
            <Container maxWidth="lg">

                {/* 1. HERO HEADER */}
                <Box sx={{ mb: 10, textAlign: { xs: "center", md: "left" } }}>
                    <Chip
                        icon={<SchoolIcon style={{ color: BRAND.green }} />}
                        label="Data Architecture · Module 09"
                        sx={{ bgcolor: "rgba(0,237,100,0.1)", color: BRAND.green, border: `1px solid ${BRAND.border}`, mb: 2 }}
                    />
                    <Typography variant="h2" sx={{ fontWeight: 900, mb: 2, letterSpacing: "-1.5px" }}>
                        Schema <span style={{ color: BRAND.green }}>Design</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: 18, maxWidth: 800, lineHeight: 1.7 }}>
                        In MongoDB, we don't design for the data; we design for the <strong>Application</strong>.
                        The way you model your documents depends entirely on how your UI needs to read and write that data.
                    </Typography>
                </Box>

                {/* 2. THE GOLDEN RULE */}
                <Box sx={{ mb: 10, p: 4, borderRadius: 6, bgcolor: "rgba(0, 237, 100, 0.03)", border: `1px solid ${BRAND.border}` }}>
                    <Stack direction="row" spacing={3} alignItems="center">
                        <EngineeringIcon sx={{ color: BRAND.green, fontSize: 48 }} />
                        <Box>
                            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>The Golden Rule</Typography>
                            <Typography variant="body1" sx={{ color: BRAND.muted }}>
                                "Data that is accessed together, should be stored together."
                                This minimizes expensive database joins and maximizes read performance.
                            </Typography>
                        </Box>
                    </Stack>
                </Box>

                {/* 3. EMBEDDING VS REFERENCING */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 6 }}>
                        <CompareArrowsIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Embedding vs. Referencing</Typography>
                    </Box>



                    <Grid container spacing={4} sx={{ mt: 2 }}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" sx={{ color: BRAND.green, mb: 2, fontWeight: 700 }}>1. Embedding (Denormalization)</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3 }}>
                                Store related data inside a single document. Best for <strong>One-to-Few</strong> relationships
                                where the data is small and rarely changes independently.
                            </Typography>
                            <CodeTerminal
                                label="Embedded Document"
                                code={`{\n  name: "John Doe",\n  address: {\n    street: "123 Main St",\n    city: "New York"\n  } // Address is EMBEDDED\n}`}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="h6" sx={{ color: BRAND.cyan, mb: 2, fontWeight: 700 }}>2. Referencing (Normalization)</Typography>
                            <Typography variant="body2" sx={{ color: BRAND.muted, mb: 3 }}>
                                Link documents using an <code>_id</code>. Best for <strong>One-to-Many</strong> (Large) or
                                <strong>Many-to-Many</strong> relationships where data grows indefinitely.
                            </Typography>
                            <CodeTerminal
                                label="Referenced Document"
                                color={BRAND.cyan}
                                code={`{\n  name: "John Doe",\n  posts: [\n    ObjectId("65f1a..."), \n    ObjectId("65f2b...") \n  ] // Array of REFERENCES\n}`}
                            />
                        </Grid>
                    </Grid>
                </Box>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.05)", mb: 10 }} />

                {/* 4. RELATIONSHIP TYPES */}
                <Box sx={{ mb: 10 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 4 }}>
                        <AccountTreeIcon sx={{ color: BRAND.green, fontSize: 32 }} />
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>Common Relationship Patterns</Typography>
                    </Box>



                    <Grid container spacing={3}>
                        {[
                            { title: "One-to-One", strategy: "Embed", desc: "User → Profile Settings" },
                            { title: "One-to-Few", strategy: "Embed", desc: "Product → Images (Max 10)" },
                            { title: "One-to-Many", strategy: "Reference", desc: "Author → 1,000+ Books" },
                            { title: "Many-to-Many", strategy: "Reference", desc: "Students ↔ Courses" }
                        ].map((pattern, i) => (
                            <Grid item xs={12} sm={6} md={3} key={pattern.title}>
                                <Paper sx={{ p: 3, bgcolor: BRAND.surface, borderRadius: 4, border: `1px solid ${BRAND.border}`, height: '100%' }}>
                                    <Typography variant="subtitle1" sx={{ color: BRAND.white, fontWeight: 800 }}>{pattern.title}</Typography>
                                    <Chip label={pattern.strategy} size="small" sx={{ my: 1, bgcolor: pattern.strategy === "Embed" ? BRAND.green : BRAND.cyan, color: BRAND.bg, fontWeight: 900 }} />
                                    <Typography variant="caption" sx={{ display: "block", color: BRAND.muted }}>{pattern.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* 5. PRO-TIP: THE 16MB LIMIT */}
                <Box sx={{ mb: 10 }}>
                    <Paper sx={{ p: 3, bgcolor: "rgba(255, 255, 255, 0.02)", border: `1px solid ${BRAND.border}`, borderRadius: 4 }}>
                        <Stack direction="row" spacing={2}>
                            <Avatar sx={{ bgcolor: BRAND.green }}><LayersIcon /></Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ color: BRAND.white, fontWeight: 700 }}>The 16MB Boundary</Typography>
                                <Typography variant="body2" sx={{ color: BRAND.muted, mt: 1 }}>
                                    A single BSON document cannot exceed <strong>16 Megabytes</strong>.
                                    If your embedded array (like comments on a viral post) could potentially
                                    grow forever, you <strong>must</strong> use referencing instead.
                                </Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Box>

                {/* FOOTER CTA */}
                <Box sx={{
                    p: 8, borderRadius: 8, textAlign: "center",
                    background: `linear-gradient(45deg, ${BRAND.bg}, #00684A)`,
                    border: `1px solid ${BRAND.border}`,
                    boxShadow: `0 20px 40px rgba(0,0,0,0.5)`
                }}>
                    <DynamicFormIcon style={{ fontSize: 40, color: BRAND.green, marginBottom: 16 }} />
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Schema Designed?</Typography>
                    <Typography sx={{ color: BRAND.muted, mb: 4 }}>
                        Now that you understand the structure, let's learn how to process
                        large amounts of data with the Aggregation Framework.
                    </Typography>
                    <Stack direction="row" spacing={2} justifyContent="center">
                        <Button variant="contained" sx={{ bgcolor: BRAND.green, color: BRAND.bg, fontWeight: 900 }}>Go to Aggregation</Button>
                        <Button variant="outlined" sx={{ color: BRAND.white, borderColor: BRAND.muted }}>Modeling Docs</Button>
                    </Stack>
                </Box>

            </Container>
        </Box>
    );
}
